/**
 * Shared hook for the employee's own leave-pattern health bar.
 *
 * Policy v1.0 §2.3: "Employees can view their own risk status and active
 * flag explanations in the My Leave section." This client wraps the
 * backend projection so the My-Leave page widget renders the band + score
 * + contributing pattern codes.
 *
 * Endpoint: GET /api/v1/me/health-bar
 * Response: { score: number; band: 'low' | 'medium' | 'high'; contributingPatterns: string[] }
 */
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { apiRequest } from './client'

// ── Zod schema (Zod at every boundary per CLAUDE.md) ─────────────────────────

// Backend serialises the band as a lowercase string (`"low"`, `"medium"`,
// `"high"` — see `src/domain/health_bar.py`). Keep the schema strict so a
// future enum drift fails loud at the boundary instead of silently
// rendering an unstyled card.
export const HealthBarBandSchema = z.enum(['low', 'medium', 'high'])
export type HealthBarBand = z.infer<typeof HealthBarBandSchema>

// Policy v1.0 §1.2 — no-carry-forward nudge. Severity widens as the
// year closes; `none` means no annual days are at risk yet, so the
// nudge does not render. The bar's score / band is unaffected — this
// is a parallel "use-it-or-lose-it" surface, not a pattern.
export const ForfeitureRiskSchema = z.object({
  daysAtRisk: z.number().int().nonnegative(),
  monthsToYearEnd: z.number().int().nonnegative(),
  severity: z.enum(['none', 'soft', 'firm']),
})
export type ForfeitureRiskData = z.infer<typeof ForfeitureRiskSchema>

// Per-pattern live values for the collapsed card copy, keyed by pattern code
// (e.g. `{ frequent_sick_leave: { sick_days: 7 } }`). Optional so a backend
// that omits it (or a pattern without a dynamic line) falls back to the static
// explainer text rather than failing the boundary.
export const PatternMetricsSchema = z.record(
  z.string(),
  z.record(z.string(), z.number()),
)
export type PatternMetrics = z.infer<typeof PatternMetricsSchema>

export const HealthBarSchema = z.object({
  score: z.number().int().nonnegative(),
  band: HealthBarBandSchema,
  contributingPatterns: z.array(z.string()),
  patternMetrics: PatternMetricsSchema.optional(),
  forfeitureRisk: ForfeitureRiskSchema.nullable().optional(),
})

export type HealthBarData = z.infer<typeof HealthBarSchema>

// ── Fetcher ───────────────────────────────────────────────────────────────────

async function fetchHealthBar(): Promise<HealthBarData> {
  const raw = await apiRequest<unknown>('/api/v1/me/health-bar')
  return HealthBarSchema.parse(raw)
}

/**
 * Employee-scoped variant — same projection, but for an arbitrary employee
 * (HR+ only, enforced server-side). Powers the "Viewing as" wellbeing card so
 * an impersonating HR user sees the impersonated employee's own band/score
 * rather than the suppressed card or their own /me data.
 */
async function fetchEmployeeHealthBar(employeeId: string): Promise<HealthBarData> {
  const raw = await apiRequest<unknown>(
    `/api/v1/employees/${encodeURIComponent(employeeId)}/health-bar`,
  )
  return HealthBarSchema.parse(raw)
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export const HEALTH_BAR_QUERY_KEY = ['me', 'health-bar'] as const

export interface UseHealthBarResult {
  data: HealthBarData | undefined
  isPending: boolean
  isError: boolean
}

/**
 * TanStack Query hook that returns the employee's leave-pattern outlook.
 *
 * - `staleTime: 5min` — pattern detection is recomputed nightly + on
 *   leave decisions, so we don't need a fresh fetch on every focus.
 * - Always enabled — My-Leave mounts this on first paint.
 *
 * Pass `employeeId` to read another employee's projection (the impersonation
 * / "Viewing as" case). Omit it (or pass `null`) for the calling user's own
 * `/me/health-bar`. The query key is scoped per-employee so the two never
 * collide in the cache.
 */
export function useHealthBar(employeeId?: string | null): UseHealthBarResult {
  const { data, isPending, isError } = useQuery({
    queryKey: employeeId
      ? (['employee', employeeId, 'health-bar'] as const)
      : HEALTH_BAR_QUERY_KEY,
    queryFn: employeeId
      ? () => fetchEmployeeHealthBar(employeeId)
      : fetchHealthBar,
    staleTime: 5 * 60_000,
  })
  return { data, isPending, isError }
}

// ── Wellbeing narrative (ILMU) ───────────────────────────────────────────────
//
// Restored post-SG#1 for the Leave Rhythm card. Headline + body come from the
// ai-service hop (warm "leave rhythm" voice, grounded in policy §2.3 / §2.2),
// served through the api with a 24h cache. When the `ilmuWellbeingNarrative`
// flag is off this hook is disabled and the card falls back to deterministic
// band-keyed copy.
//
// Mirrors GET /api/v1/me/wellbeing-narrative (own card) and
// GET /api/v1/employees/{id}/wellbeing-narrative (HR "Viewing as").

export const WellbeingNarrativeSchema = z.object({
  headline: z.string(),
  body: z.string(),
  invocationId: z.string(),
})
export type WellbeingNarrative = z.infer<typeof WellbeingNarrativeSchema>

async function fetchWellbeingNarrative(): Promise<WellbeingNarrative> {
  const raw = await apiRequest<unknown>('/api/v1/me/wellbeing-narrative')
  return WellbeingNarrativeSchema.parse(raw)
}

async function fetchEmployeeWellbeingNarrative(
  employeeId: string,
): Promise<WellbeingNarrative> {
  const raw = await apiRequest<unknown>(
    `/api/v1/employees/${encodeURIComponent(employeeId)}/wellbeing-narrative`,
  )
  return WellbeingNarrativeSchema.parse(raw)
}

export const WELLBEING_NARRATIVE_QUERY_KEY = ['me', 'wellbeing-narrative'] as const

export interface UseWellbeingNarrativeResult {
  data: WellbeingNarrative | undefined
  isPending: boolean
  /** True only while the request is actively in flight with no data yet.
   *  Unlike `isPending`, this is false when the query is disabled, so callers
   *  can show a skeleton for real loading (not for the flag-off case). */
  isLoading: boolean
}

/**
 * Wellbeing narrative for the Leave Rhythm card.
 *
 * @param enabled     gate on the `ilmuWellbeingNarrative` flag; disables the
 *                    query so the card renders deterministic copy when off.
 * @param employeeId  pass to read another employee's narrative (HR "Viewing
 *                    as"); omit / null for the calling user's own `/me`. The
 *                    query key is scoped per-employee so the two never collide.
 */
export function useWellbeingNarrative(
  enabled: boolean,
  employeeId?: string | null,
): UseWellbeingNarrativeResult {
  const { data, isPending, isLoading } = useQuery({
    queryKey: employeeId
      ? (['employee', employeeId, 'wellbeing-narrative'] as const)
      : WELLBEING_NARRATIVE_QUERY_KEY,
    queryFn: employeeId
      ? () => fetchEmployeeWellbeingNarrative(employeeId)
      : fetchWellbeingNarrative,
    staleTime: 5 * 60_000,
    enabled,
  })
  return { data, isPending, isLoading }
}
